import { call, put, select } from 'redux-saga/effects'
import { ApiResponse } from 'apisauce'

import api from 'src/services/api'

import { ApplicationState } from 'src/core/store'
import { ClientNotifyCatch } from 'src/config/bugsnag'
import {
  ResponseDataRequests,
  ResponseDataRequestsItem
} from 'src/core/store/ducks/requests/types'

import { ResourcesHttpData, Report, R4Data, R4Header } from './types'
import * as Actions from './actions'

type SubTraveler = {
  name: string
  reason: string
  update_on: number
  status: string
}

function abbreviate(word: string, index: number): string {
  if (index) {
    if (/[A-Z]/.test(word.charAt(0))) {
      return `${word.charAt(0)}.`
    }

    if (/[0-9]/.test(word.charAt(0))) {
      return word
    }
    return ''
  }
  return word
}

/**
 * @todo
 * getR4DataInfos it was necessary as an exit to fetch /requests
 * instead of a resource as it should be
 */
function getR4DataInfos(requests: ResponseDataRequestsItem[]) {
  const data: R4Data[] = []
  requests.forEach(
    ({
      empowered_users,
      request_number,
      request_user_data,
      requested_on,
      status,
      title
    }) => {
      const traveler = request_user_data
        .reduce(
          (acc, cur) => [
            ...acc,
            {
              name:
                cur.traveler?.name.split(' ').map(abbreviate).join(' ') || '',
              reason: cur.reason?.name || '',
              status: cur.status,
              update_on: cur.updated_on
            }
          ],
          [] as SubTraveler[]
        )
        .sort((a, b) => {
          const hasAUnderway = a.status === 'UNDERWAY'
          const hasBUnderway = b.status === 'UNDERWAY'
          const updateOnResult = a.update_on - b.update_on

          if (hasAUnderway && hasBUnderway) {
            return updateOnResult
          }

          if (hasAUnderway) {
            return 1
          }
          return hasBUnderway ? -1 : updateOnResult
        })

      const owner = empowered_users.reduce(
        (acc, cur) => [...acc, cur.name],
        [] as string[]
      )
      data.push({
        description: title,
        owner,
        request_date: requested_on,
        request_number: Number(request_number),
        status,
        traveler
      })
    }
  )
  return data.sort((a, b) => {
    const hasAUnderway = a.traveler.some(({ status }) => status === 'UNDERWAY')
    const hasBUnderway = b.traveler.some(({ status }) => status === 'UNDERWAY')
    const updateOnResult = a.traveler[0].update_on - b.traveler[0].update_on

    if (hasAUnderway && hasBUnderway) {
      return updateOnResult
    }

    if (hasAUnderway) {
      return 1
    }
    return hasBUnderway ? -1 : updateOnResult
  })
}

export function* getReports({
  payload: { report_id }
}: ReturnType<typeof Actions.getReports>) {
  try {
    const {
      organization: { organization_id }
    } = yield select((state: ApplicationState) => state.me)
    const filters = report_id ? `?number=${report_id}` : ''

    const { data, ok }: ApiResponse<{ report_list: Report[] }> = yield call(
      api.get,
      `organizations/${organization_id}/reports${filters}`
    )

    if (ok) {
      yield put(Actions.getReportsSuccess(data.report_list))
      return
    }

    yield put(Actions.getReportsFailure())
  } catch (err) {
    ClientNotifyCatch('Saga getReports', err)
    yield put(Actions.getReportsFailure())
  }
}

export function* getReportsResource({
  payload: { resource, query_string: baseQuery }
}: ReturnType<typeof Actions.getReportsResource>) {
  try {
    const {
      organization: { organization_id }
    } = yield select((state: ApplicationState) => state.me)

    if (resource === 'R4Data') {
      const urlParams = new URLSearchParams(baseQuery.split('?')?.[1])
      const ended_on = urlParams.get('ended_on')
      const paramOrganizationId = urlParams.get('organization_id')
      const started_on = urlParams.get('started_on')
      let status = urlParams.get('status')

      if ([null, 'NULL'].includes(status)) {
        status = 'underway,approval_pending,conference_pending,closing_pending'
      }

      status = status
        .split(',')
        .map(item => `${item}=True`)
        .join('&')

      const query_string = `?organization_id=${paramOrganizationId}&started_on=${started_on}&ended_on=${ended_on}&${status}&page_size=999`

      const { data: R4Data, ok }: ApiResponse<ResponseDataRequests> =
        yield call(
          api.get,
          `/organizations/${organization_id}/pendencies/requests/report${query_string}`
        )

      if (ok) {
        const R4DataInfo = getR4DataInfos(R4Data.requests)
        const R4HeaderInfo: R4Header[] = [
          {
            ended_on: Number(ended_on),
            requests: R4Data.total_results,
            started_on: Number(started_on),
            status: urlParams.get('status')
          }
        ]
        yield put(Actions.getReportsResourceSuccess('R4Header', R4HeaderInfo))
        yield put(Actions.getReportsResourceSuccess('R4Data', R4DataInfo))
        return
      }
      yield put(Actions.getReportsResourceFailure('R4Header'))
      yield put(Actions.getReportsResourceFailure('R4Data'))
      return
    }

    const query_string = `${
      baseQuery ? `${baseQuery}&` : '?'
    }organization_id=${organization_id}`

    const { data, ok }: ApiResponse<ResourcesHttpData<any>> = yield call(
      api.get,
      `organizations/${organization_id}/reports/${query_string}`
    )

    if (ok) {
      yield put(Actions.getReportsResourceSuccess(resource, data.report_data))
      return
    }

    yield put(Actions.getReportsResourceFailure(resource))
  } catch (err) {
    ClientNotifyCatch('Saga getReportsResource', err)
    yield put(Actions.getReportsResourceFailure(resource))
  }
}
